import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, X } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { resolveImg } from "@/contexts/SettingsContext";
import ImageUploadField from "@/components/admin/ImageUploadField";

const field = "vl-input rounded-lg px-4 py-2.5 w-full";
const empty = { title: "", excerpt: "", body: "", cover: "", published: true };

export default function BlogPanel() {
  const { t } = useLang();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/blog").then((r) => setPosts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.title) { toast.error(t("required_field")); return; }
    try {
      if (editing.id) await api.put(`/admin/blog/${editing.id}`, editing);
      else await api.post("/admin/blog", editing);
      toast.success(t("save")); setEditing(null); load();
    } catch (e) { toast.error(errMsg(e.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/admin/blog/${id}`); setPosts((x) => x.filter((p) => p.id !== id)); } catch (e) { toast.error(errMsg(e.response?.data?.detail)); } };

  if (loading) return <div className="py-16 text-center"><Loader2 className="animate-spin text-[var(--vl-gold)] mx-auto" /></div>;

  return (
    <div data-testid="blog-panel">
      <button onClick={() => setEditing({ ...empty })} className="vl-btn-gold rounded-full px-5 py-2.5 text-sm font-medium flex items-center gap-2 mb-5" data-testid="add-blog-btn"><Plus size={15} /> {t("add_item")}</button>
      <div className="space-y-3">
        {posts.map((p) => (
          <div key={p.id} className="vl-card rounded-xl p-4 flex items-center gap-4" data-testid={`admin-blog-${p.id}`}>
            {p.cover && <img src={resolveImg(p.cover)} alt="" className="w-16 h-16 rounded object-cover" />}
            <div className="flex-1 min-w-0">
              <h4 className="text-[var(--vl-ivory)] font-medium">{p.title}</h4>
              <div className="text-xs text-[var(--vl-stone)]">{new Date(p.date).toLocaleDateString()} · {p.published ? t("published_label") : "—"}</div>
            </div>
            <button onClick={() => setEditing(p)} className="vl-btn-outline rounded-full px-4 py-1.5 text-xs">{t("edit_label")}</button>
            <button onClick={() => del(p.id)} className="text-red-300 px-2"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-6">
          <div className="absolute inset-0 bg-black/70" onClick={() => setEditing(null)} />
          <div className="relative vl-card rounded-2xl p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto" data-testid="blog-editor">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-serif-display text-xl text-[var(--vl-ivory)]">{t("blog_tab")}</h3>
              <button onClick={() => setEditing(null)}><X size={20} className="text-[var(--vl-stone)]" /></button>
            </div>
            <div className="space-y-3">
              <input className={field} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder={t("title_label")} data-testid="blog-title" />
              <input className={field} value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} placeholder={t("excerpt_label")} />
              <textarea className={field} rows={8} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} placeholder={t("body_label")} data-testid="blog-body" />
              <div><label className="block text-xs tracking-widest uppercase text-[var(--vl-stone)] mb-2">{t("image_label")}</label><ImageUploadField value={editing.cover} onChange={(url) => setEditing({ ...editing, cover: url })} testid="blog-cover-upload" /></div>
              <label className="flex items-center gap-2 text-sm text-[var(--vl-ivory)]"><input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} /> {t("published_label")}</label>
            </div>
            <button onClick={save} className="vl-btn-gold rounded-full px-6 py-2.5 text-sm font-medium mt-5 w-full" data-testid="save-blog-btn">{t("save")}</button>
          </div>
        </div>
      )}
    </div>
  );
}
